import React, { useState, useEffect, useContext } from "react";
import "./dividend.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import dbService from "../../services/database";
import Loading from "../../components/Loading/Loading";
import { useLoggedUser } from "../../context/LoggedUserContext";
import { AuthContext } from "../../context/AuthContext";
import useRolePermissions from "../../hooks/useRolePermissions";
import { CSVLink } from "react-csv";
import { SearchContext } from "../../context/SearchContext";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import GetAppIcon from "@mui/icons-material/GetApp";
import RefreshIcon from "@mui/icons-material/Refresh";
import PeopleIcon from "@mui/icons-material/People";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PercentIcon from "@mui/icons-material/Percent";

// Helper function to clean and format share values
const cleanShareValue = (shareStr) => {
  if (!shareStr) return 0;
  const str = String(shareStr);
  const cleaned = str.replace(/[^0-9.]/g, "");
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

const Dividend = () => {
  const [dividendRecords, setDividendRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { loggedUser } = useLoggedUser();
  const { isReadOnlyRole } = useRolePermissions();
  const DIVIDEND_MULTIPLIER = 0.09384;
  const DIVIDEND_PERCENTAGE = 9.384;
  const [currentPage, setCurrentPage] = useState(1);
  const [year, setYear] = useState(2023); // Default to 2023 since that's where we have data
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [allDividends, setAllDividends] = useState([]);
  const [filteredDividends, setFilteredDividends] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [sortField, setSortField] = useState("userName");
  const [sortOrder, setSortOrder] = useState("asc");
  const { search } = useContext(SearchContext);
  const { currentUser, isLoading: authLoading } = useContext(AuthContext);
  const [dividendsExist, setDividendsExist] = useState(false);
  const [dividendSummary, setDividendSummary] = useState(null);

  // Calculation functions
  const calculateTotalShares = () => {
    return filteredDividends.reduce((total, dividend) => {
      const share = parseFloat(dividend.shareValue) || 0;
      return Number((total + share).toFixed(2));
    }, 0);
  };

  const calculateTotalDividends = () => {
    return filteredDividends.reduce((total, dividend) => {
      const dividendAmount = parseFloat(dividend.dividendAmount) || 0;
      return Number((total + dividendAmount).toFixed(2));
    }, 0);
  };

  useEffect(() => {
    // Wait for auth to be ready before fetching data
    if (authLoading) {
      console.log("⏳ Waiting for auth before fetching dividends...");
      return;
    }

    if (!currentUser) {
      console.log("⚠️ No authenticated user, skipping dividend fetch");
      setIsLoading(false);
      return;
    }

    const fetchDividendData = async () => {
      try {
        setIsLoading(true);
        setError("");

        console.log(`🔄 Fetching dividend data for year ${year}...`);

        // Fetch dividend records for the selected year
        const dividendsResponse = await dbService.getDividendsByYear(year);
        console.log("Raw dividend response:", dividendsResponse);

        // Handle different response formats
        let dividends = [];
        if (Array.isArray(dividendsResponse)) {
          dividends = dividendsResponse;
        } else if (dividendsResponse && dividendsResponse.data) {
          dividends = dividendsResponse.data;
        } else if (
          dividendsResponse &&
          dividendsResponse.success &&
          dividendsResponse.data
        ) {
          dividends = dividendsResponse.data;
        }

        console.log(
          `📋 Processed ${dividends.length} dividend records for year ${year}`
        );

        if (dividends && dividends.length > 0) {
          // Transform the data to ensure consistent format
          const transformedDividends = dividends.map((dividend) => ({
            id: dividend.id,
            userId: dividend.userId || dividend.user_id,
            userName:
              dividend.userName ||
              dividend.user_name ||
              dividend.display_name ||
              "Unknown User",
            userPhone: dividend.userPhone || dividend.phone || "N/A",
            userEmail:
              dividend.userEmail || dividend.email || dividend.userId || "N/A",
            shareValue: parseFloat(
              dividend.shareValue || dividend.share_value || 0
            ),
            dividendAmount: parseFloat(
              dividend.dividendAmount || dividend.dividend_amount || 0
            ),
            processedDate:
              dividend.processedDate ||
              dividend.processed_at ||
              dividend.payment_date ||
              new Date(),
            processedBy:
              dividend.processedBy || dividend.processed_by || "System",
            status: dividend.status || "processed",
            userStatus: dividend.userStatus || "processed",
            year: dividend.year || year,
            paymentReference:
              dividend.paymentReference ||
              dividend.payment_reference ||
              `DIV-${year}-${dividend.id}`,
          }));

          setAllDividends(transformedDividends);
          setFilteredDividends(transformedDividends);
          setDividendsExist(true);
        } else {
          setAllDividends([]);
          setFilteredDividends([]);
          setDividendsExist(false);
        }

        // Fetch dividend summary
        try {
          const summaryResponse = await dbService.getDividendSummary(year);
          console.log("Dividend summary response:", summaryResponse);

          let summary = null;
          if (summaryResponse && summaryResponse.data) {
            summary = summaryResponse.data;
          } else if (summaryResponse && !summaryResponse.error) {
            summary = summaryResponse;
          }

          setDividendSummary(summary);
        } catch (summaryError) {
          console.warn("Could not fetch dividend summary:", summaryError);
          setDividendSummary(null);
        }

        setIsLoading(false);
      } catch (error) {
        console.error("❌ Error fetching dividend data:", error);
        setError("Error fetching dividend data: " + error.message);
        setIsLoading(false);
      }
    };

    fetchDividendData();
  }, [year, authLoading, currentUser]);

  // Enhanced filtering function
  useEffect(() => {
    let filtered = [...allDividends];

    // Text search filter
    if (searchTerm || search) {
      const searchQuery = searchTerm || search;
      filtered = filtered.filter(
        (dividend) =>
          dividend.userName
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          dividend.userId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          dividend.userEmail?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(
        (dividend) => dividend.userStatus === statusFilter
      );
    }

    // Sort dividends
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === "shareValue" || sortField === "dividendAmount") {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredDividends(filtered);
    setCurrentPage(1);
  }, [allDividends, searchTerm, search, statusFilter, sortField, sortOrder]);

  const processDividends = async () => {
    if (isReadOnlyRole) {
      setError("You do not have permission to process dividends");
      return;
    }

    const currentYear = new Date().getFullYear();
    if (year > currentYear) {
      setError(`Cannot process dividends for future year ${year}`);
      return;
    }

    setIsProcessing(true);
    setError("");
    setSuccess("");

    try {
      // Check if dividends already exist for this year
      const existsResponse = await dbService.checkDividendsExist(year);

      if (existsResponse.exists) {
        setError(`Dividends for year ${year} have already been processed`);
        setIsProcessing(false);
        return;
      }

      // Process dividends via API
      const result = await dbService.processDividends(
        year,
        DIVIDEND_MULTIPLIER,
        loggedUser?.name || "Unknown"
      );

      if (result.success) {
        setSuccess(
          `Successfully processed dividends for year ${year}. ${
            result.data?.processed_count || "Multiple"
          } users processed.`
        );
        setDividendsExist(true);
        // Refresh the dividend data
        window.location.reload();
      } else {
        setError(result.error || "Failed to process dividends");
      }
    } catch (error) {
      console.error("Error processing dividends:", error);
      setError("Error processing dividends: " + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const checkDividendsExist = async (selectedYear) => {
    try {
      const response = await dbService.checkDividendsExist(selectedYear);
      setDividendsExist(response.exists);
    } catch (error) {
      console.error("Error checking dividends:", error);
    }
  };

  const handleYearChange = async (e) => {
    const selectedYear = Number(e.target.value);
    setYear(selectedYear);
    await checkDividendsExist(selectedYear);
  };

  const getCurrentPageUsers = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredDividends.slice(startIndex, startIndex + itemsPerPage);
  };

  const downloadCSV = () => {
    return filteredDividends.map((user, index) => ({
      "#": index + 1,
      Name: user.userName,
      Email: user.userEmail,
      Phone: user.userPhone,
      "Share Amount": user.shareValue,
      "Dividend Amount (GH₵)": user.dividendAmount,
      Status: user.userStatus,
      "Payment Reference": user.paymentReference,
      "Processed By": user.processedBy,
      "Processed Date": new Date(user.processedDate).toLocaleDateString(),
      Year: year,
    }));
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const totalPages = Math.ceil(filteredDividends.length / itemsPerPage);
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const start = Math.max(1, currentPage - 2);
      const end = Math.min(totalPages, start + maxVisiblePages - 1);

      if (start > 1) {
        pageNumbers.push(1);
        if (start > 2) pageNumbers.push("...");
      }

      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }

      if (end < totalPages) {
        if (end < totalPages - 1) pageNumbers.push("...");
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  const renderYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 10; i--) {
      years.push(
        <option key={i} value={i}>
          {i}
        </option>
      );
    }
    return years;
  };

  const totalPages = Math.ceil(filteredDividends.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  if (isLoading)
    return <Loading inline={true} message="Loading dividend records..." />;

  return (
    <div className="dividend">
      <Sidebar />
      <div className="dividendContainer">
        <Navbar />

        {/* Header Section */}
        <div className="page-header">
          <div className="header-content">
            <div className="title-section">
              <AccountBalanceWalletIcon className="icon" />
              <div className="title-text">
                <h1>Dividend Records</h1>
                <p>View processed dividend payments to shareholders</p>
              </div>
            </div>
            <div className="header-actions">
              <CSVLink
                data={downloadCSV()}
                filename={`dividends-${year}.csv`}
                className="export-btn"
              >
                <GetAppIcon /> Export CSV
              </CSVLink>
              <button
                className="filter-toggle-btn"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FilterListIcon /> {showFilters ? "Hide" : "Show"} Filters
              </button>
            </div>
          </div>
        </div>

        <div className="dividendContent">
          {/* Year Selector & Status */}
          <div className="yearSection">
            <div className="yearSelector">
              <label>Processing Year:</label>
              <select value={year} onChange={handleYearChange}>
                {renderYearOptions()}
              </select>
            </div>
            {dividendsExist && (
              <div className="warning">
                ⚠️ Dividends for {year} have already been processed
              </div>
            )}
            {!dividendsExist && allDividends.length === 0 && (
              <div className="info">
                📋 No dividend records found for {year}. You may need to process
                dividends first.
              </div>
            )}
          </div>

          {/* Filters Section */}
          {showFilters && (
            <div className="filters-section">
              <div className="filters-grid">
                <div className="filter-group">
                  <label>Search</label>
                  <div className="search-input">
                    <SearchIcon />
                    <input
                      type="text"
                      placeholder="Search by name, email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                <div className="filter-group">
                  <label>Status Filter</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Records</option>
                    <option value="processed">Processed</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Items per page</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>

                <div className="filter-actions">
                  <button onClick={clearFilters} className="clear-btn">
                    <RefreshIcon /> Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Stats Section */}
          <div className="statsSection">
            <div className="stat">
              <div className="stat-icon">
                <PeopleIcon />
              </div>
              <div className="stat-content">
                <div className="label">Dividend Recipients ({year})</div>
                <div className="value">
                  {filteredDividends.length.toLocaleString()}
                </div>
              </div>
            </div>
            <div className="stat">
              <div className="stat-icon">
                <TrendingUpIcon />
              </div>
              <div className="stat-content">
                <div className="label">Total Share Value (GH₵)</div>
                <div className="value">
                  {calculateTotalShares().toLocaleString()}
                </div>
              </div>
            </div>
            <div className="stat">
              <div className="stat-icon">
                <MonetizationOnIcon />
              </div>
              <div className="stat-content">
                <div className="label">Total Dividends</div>
                <div className="value">
                  GH₵{" "}
                  {calculateTotalDividends().toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                  })}
                </div>
              </div>
            </div>
            <div className="stat">
              <div className="stat-icon">
                <PercentIcon />
              </div>
              <div className="stat-content">
                <div className="label">Dividend Rate</div>
                <div className="value">{DIVIDEND_PERCENTAGE}%</div>
              </div>
            </div>
          </div>

          {/* Action Section */}
          <div className="dividendActions">
            <button
              className={`processBtn ${
                isReadOnlyRole || dividendsExist ? "disabled" : ""
              }`}
              onClick={processDividends}
              disabled={isProcessing || isReadOnlyRole || dividendsExist}
            >
              {isProcessing ? (
                <div className="processingStatus">
                  Processing {year} Dividends...
                </div>
              ) : (
                `Process ${year} Dividends`
              )}
            </button>

            {error && <div className="error">{error}</div>}
            {success && <div className="success">{success}</div>}
          </div>

          {/* Results Summary */}
          <div className="results-summary">
            <div className="summary-text">
              Showing {startIndex + 1} to{" "}
              {Math.min(endIndex, filteredDividends.length)} of{" "}
              {filteredDividends.length} dividend records for {year}
              {searchTerm || statusFilter ? (
                <span className="filtered-text">
                  {" "}
                  (filtered from {allDividends.length} total)
                </span>
              ) : (
                ""
              )}
            </div>
          </div>

          {/* Dividend Table */}
          <div className="dividendTable">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th
                    className={`sortable ${
                      sortField === "userName" ? `sorted-${sortOrder}` : ""
                    }`}
                    onClick={() => handleSort("userName")}
                  >
                    Name
                  </th>
                  {/* <th>User Details</th> */}
                  <th
                    className={`sortable ${
                      sortField === "shareValue" ? `sorted-${sortOrder}` : ""
                    }`}
                    onClick={() => handleSort("shareValue")}
                  >
                    Shares
                  </th>
                  <th>Dividend (GH₵)</th>
                  <th>Status</th>
                  <th>Payment Reference</th>
                  <th
                    className={`sortable ${
                      sortField === "processedDate" ? `sorted-${sortOrder}` : ""
                    }`}
                    onClick={() => handleSort("processedDate")}
                  >
                    Processed Date
                  </th>
                </tr>
              </thead>
              <tbody>
                {getCurrentPageUsers().length > 0 ? (
                  getCurrentPageUsers().map((user, index) => (
                    <tr key={user.id}>
                      <td className="row-number">{startIndex + index + 1}</td>
                      <td className="name-cell">
                        <div className="name">{user.userName}</div>
                      </td>
                      {/* <td className="contact-cell">
                        <div className="email">Email: {user.userEmail}</div>
                        <div className="phone">Phone: {user.userPhone}</div>
                        <div className="processed-by">
                          Processed By: {user.processedBy}
                        </div>
                      </td> */}
                      <td className="shares">
                        {Number(user.shareValue).toLocaleString()}
                      </td>
                      <td className="shares">
                        {" "}
                        {Number(user.dividendAmount).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                        })}
                      </td>
                      <td className="status">
                        <span className="status-badge status-processed">
                          {user.status}
                        </span>
                      </td>
                      <td className="reference">{user.paymentReference}</td>
                      <td className="processed-date">
                        {new Date(user.processedDate).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="no-data">
                      {searchTerm || statusFilter
                        ? "No dividend records found matching your filters."
                        : allDividends.length === 0
                        ? `No dividend records found for ${year}. You may need to process dividends first.`
                        : "No dividend records available."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="page-btn first"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="page-btn prev"
                >
                  Previous
                </button>

                {getPageNumbers().map((pageNum, idx) => (
                  <button
                    key={idx}
                    onClick={() =>
                      typeof pageNum === "number" && setCurrentPage(pageNum)
                    }
                    className={`page-btn ${
                      pageNum === currentPage ? "active" : ""
                    } ${typeof pageNum !== "number" ? "dots" : ""}`}
                    disabled={typeof pageNum !== "number"}
                  >
                    {pageNum}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="page-btn next"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="page-btn last"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dividend;
